import React, { useCallback, useMemo } from 'react';
import { Button, Tooltip } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BookmarkPromptIcon } from 'src/assets/icons/BookmarkPromptIcon';
import { IconClose } from 'src/assets/icons/IconClose';
import { IconDocument } from 'src/assets/icons/IconDocument';
import { ImproveIcon } from 'src/assets/icons/ImproveIcon';
import { Loader } from 'src/components/Loader';
import { ResourceThumbnailTooltip } from 'src/components/ResourceThumbnailTooltip';
import { ResourceTypeIcon } from 'src/components/ResourceTypeIcon';
import { htmlToSlate, slateToText } from 'src/components/RichSlate';
import { PROMPT_NODE_ID, removeNodeFromPromptById } from 'src/components/RichSlate/utils';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { SUGGEST_PROMPT_TYPES } from 'src/data/suggestPrompt';

import { PersonalizeButton } from './components/PersonalizeButton';
import { useStyles } from './useStyles';

export const Footer = ({
  isSuggestPromptLoading,
  onSuggestPromptClick,
  onSave,
  promptDocuments,
  promptText,
  isUploadingDocument,
  editor,
  hideActions,
  index,
  isPersonalizedPrompt,
  ratingsCount,
  generationTool,
  onClearVariationRatings,
  showPersonalizePrompt,
  showImprovePrompt,
  removePromptDocument,
  removePromptImage,
  isSlim,
  uploadingDocuments,
  uploadingImages,
  onOpenResourceInNewTab,
}) => {
  const styles = useStyles({ isUploadingDocument, promptDocuments, isSlim });

  const [loadingType, setLoadingType] = React.useState(null);

  const isPromptTextEmpty = useMemo(
    () => slateToText(htmlToSlate(promptText)).trim().length === 0,
    [promptText]
  );

  const onPersonalizePromptClick = useCallback(() => {
    setLoadingType(SUGGEST_PROMPT_TYPES.personalize);
    onSuggestPromptClick(SUGGEST_PROMPT_TYPES.personalize, editor);
  }, [onSuggestPromptClick, editor]);

  return (
    <div>
      <div
        className={cx(styles.attachmentsWrapper, {
          [styles.attachmentsWrapperNoActions]: hideActions,
        })}
      >
        {uploadingImages &&
          uploadingImages?.map((promptImage) =>
            promptImage.id.startsWith('uploadImage') ? (
              <div
                className={styles.promptImageLoading}
                key={promptImage.id}
              >
                <Loader size={16} />
              </div>
            ) : (
              <div key={promptImage.id}>
                <ResourceThumbnailTooltip
                  placement="bottom-start"
                  label={promptImage?.label}
                  onClick={() => onOpenResourceInNewTab(promptImage)}
                  thumbnailUrl={promptImage?.file?.thumbnailUrl}
                >
                  <div
                    className={styles.promptImage}
                    style={{
                      backgroundImage: 'url("' + promptImage?.file?.thumbnailUrl + '")',
                    }}
                  >
                    <IconClose
                      className={styles.removeElement}
                      onClick={() => {
                        removeNodeFromPromptById(
                          editor,
                          PROMPT_NODE_ID.image,
                          promptImage.label || promptImage.name
                        );
                        removePromptImage(promptImage.id, index);
                      }}
                    />
                  </div>
                </ResourceThumbnailTooltip>
              </div>
            )
          )}
        {uploadingDocuments?.map((promptDocument) =>
          promptDocument.id.startsWith('uploadDocument') ? (
            <Tooltip
              key={promptDocument.id}
              classes={{ tooltip: styles.tooltip }}
              title={promptDocument?.label || promptDocument?.name}
              placement="top"
              arrow
              interactive
            >
              <div className={styles.promptDocument}>
                <IconDocument className={styles.documentIcon} />
                <div className={styles.documentLabel}>
                  {promptDocument?.label || promptDocument?.name}
                </div>
                <Loader
                  size={16}
                  className={styles.documentLoader}
                />
              </div>
            </Tooltip>
          ) : (
            <ResourceThumbnailTooltip
              classes={{ tooltip: styles.documentTooltip }}
              thumbnailUrl={promptDocument?.file?.thumbnailUrl}
              onClick={() => onOpenResourceInNewTab(promptDocument)}
              label={promptDocument?.label || promptDocument?.name}
              placement="bottom-start"
              key={promptDocument.id}
            >
              <div className={styles.promptDocument}>
                <ResourceTypeIcon
                  className={styles.documentIcon}
                  type={RESOURCE_CONTENT_TYPE.document}
                  fileType={promptDocument.file?.fileType}
                  small
                />
                <div className={styles.documentLabel}>
                  {promptDocument?.label || promptDocument?.name}
                </div>
                <span>
                  <IconClose
                    className={styles.removeElement}
                    onClick={() => {
                      removeNodeFromPromptById(
                        editor,
                        PROMPT_NODE_ID.document,
                        promptDocument.label || promptDocument.name
                      );
                      removePromptDocument(promptDocument.id, index);
                    }}
                  />
                </span>
              </div>
            </ResourceThumbnailTooltip>
          )
        )}
      </div>
      {!hideActions && (
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {showImprovePrompt && (
              <Tooltip
                classes={{ tooltip: styles.tooltip }}
                title={'Improve prompt with AI'}
                placement="bottom"
                arrow
              >
                <Button
                  className={cx(styles.footerButton, styles.improveButton)}
                  onClick={() => {
                    setLoadingType(SUGGEST_PROMPT_TYPES.improve);
                    onSuggestPromptClick(SUGGEST_PROMPT_TYPES.improve, editor);
                  }}
                  startIcon={
                    isSuggestPromptLoading && loadingType === SUGGEST_PROMPT_TYPES.improve ? (
                      <Loader size={16} />
                    ) : (
                      <ImproveIcon className={styles.improveStartIcon} />
                    )
                  }
                  classes={{ startIcon: styles.startIcon }}
                  disabled={isPromptTextEmpty || isSuggestPromptLoading}
                >
                  Improve
                </Button>
              </Tooltip>
            )}

            {showPersonalizePrompt &&
              generationTool !== GENERATION_TOOL.freestyle &&
              generationTool !== GENERATION_TOOL.generalProductListing && (
                <PersonalizeButton
                  ratingsCount={ratingsCount}
                  generationTool={generationTool}
                  onClearVariationRatings={onClearVariationRatings}
                  onPersonalizePromptClick={onPersonalizePromptClick}
                  disabled={isPersonalizedPrompt || isPromptTextEmpty || isSuggestPromptLoading}
                  loading={
                    isSuggestPromptLoading && loadingType === SUGGEST_PROMPT_TYPES.personalize
                  }
                />
              )}

            <Tooltip
              classes={{ tooltip: styles.tooltip }}
              title={'Save prompt'}
              placement="bottom"
              arrow
            >
              <Button
                className={styles.footerButton}
                onClick={onSave}
                startIcon={<BookmarkPromptIcon className={styles.saveStartIcon} />}
                classes={{ startIcon: styles.startIcon }}
                disabled={!slateToText(htmlToSlate(promptText)).trim()}
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

Footer.propTypes = {
  isSuggestPromptLoading: PropTypes.bool,
  onSuggestPromptClick: PropTypes.func,
  onSave: PropTypes.func,
  promptDocuments: PropTypes.shape(),
  promptText: PropTypes.string,
  isUploadingDocument: PropTypes.bool,
  hideActions: PropTypes.bool,
  editor: PropTypes.shape().isRequired,
  index: PropTypes.number,
  isPersonalizedPrompt: PropTypes.bool,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  ratingsCount: PropTypes.shape(),
  showPersonalizePrompt: PropTypes.bool,
  onClearVariationRatings: PropTypes.func,
  removePromptDocument: PropTypes.func,
  removePromptImage: PropTypes.func,
  isSlim: PropTypes.bool,
  showImprovePrompt: PropTypes.bool,
  uploadingDocuments: PropTypes.arrayOf(PropTypes.shape()),
  uploadingImages: PropTypes.arrayOf(PropTypes.shape()),
  onOpenResourceInNewTab: PropTypes.func.isRequired,
};

Footer.defaultProps = {
  isSuggestPromptLoading: false,
  onSuggestPromptClick: null,
  onSave: null,
  promptDocuments: null,
  promptText: undefined,
  isUploadingDocument: false,
  hideActions: false,
  index: 0,
  isPersonalizedPrompt: false,
  generationTool: null,
  ratingsCount: null,
  showPersonalizePrompt: false,
  onClearVariationRatings: null,
  removePromptDocument: null,
  removePromptImage: null,
  isSlim: false,
  showImprovePrompt: false,
  uploadingDocuments: [],
  uploadingImages: [],
};
